import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState } from "react";
import { FaStar } from "react-icons/fa6";
import { BsCheckCircleFill, BsExclamationCircle } from "react-icons/bs";
import { useNavigate, useParams } from "react-router-dom";
import logo from "../../images/mglLogo.png";
import axiosClient from "../../api/api";
import { PrimaryContext } from "../../components/MainContext/MainContext";

/**
 * The review page a customer lands on from the Rate Us follow-up.
 *
 * The URL carries a single-use token, not a user id: the server says who the
 * customer is and whether the link is still good, and spends it on submit.
 * A used, expired or made-up link gets a clear message, never a form.
 */
const REDIRECT_SECONDS = 6;

const Review = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = useParams();
  const { setLanguage, language } = useContext(PrimaryContext);

  const [state, setState] = useState("loading"); // loading | valid | used | expired | unknown | done
  const [name, setName] = useState("");
  const [score, setScore] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [left, setLeft] = useState(REDIRECT_SECONDS);

  useEffect(() => {
    axiosClient
      .get(`/rateus/invite/${encodeURIComponent(token || "")}`)
      .then(({ data }) => {
        setState(data.state || "unknown");
        setName(data.name || "");
      })
      .catch((err) => setState(err?.response?.data?.state || "unknown"));
  }, [token]);

  // Thank-you screen counts down, then goes home.
  useEffect(() => {
    if (state !== "done") return;
    if (left <= 0) {
      navigate("/");
      return;
    }
    const id = setTimeout(() => setLeft((n) => n - 1), 1000);
    return () => clearTimeout(id);
  }, [state, left, navigate]);

  const submit = () => {
    if (!score) {
      setError(t("rate_choose_score"));
      return;
    }
    setError("");
    setBusy(true);
    axiosClient
      .post(`/rateus`, { token, score, comment })
      .then(() => setState("done"))
      .catch((err) => {
        const e = err?.response?.data?.error;
        if (e === "used" || e === "expired" || e === "unknown") setState(e);
        else setError(t("error"));
      })
      .finally(() => setBusy(false));
  };

  const labels = [
    "",
    t("rate_score_1"),
    t("rate_score_2"),
    t("rate_score_3"),
    t("rate_score_4"),
    t("rate_score_5"),
  ];

  return (
    <div className="min-h-screen w-full bg-slate-50 flex items-center justify-center p-6 sm:p-3">
      <div className="w-full max-w-[480px] bg-white rounded-3xl shadow-[0_10px_40px_-15px_rgba(0,0,0,0.15)] border border-slate-100 p-8 sm:p-5 flex flex-col gap-6">
        <div className="flex items-center justify-between">
          <img src={logo} alt="MGL Parcel" className="w-[140px]" />
          <div className="flex items-center gap-3 text-sm font-bold">
            {["ge", "en"].map((l) => (
              <button
                key={l}
                onClick={() => setLanguage(l)}
                className={language === l ? "text-teal_600" : "text-slate-400"}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {state === "loading" && (
          <div className="py-12 text-center text-slate-400">…</div>
        )}

        {(state === "used" || state === "expired" || state === "unknown") && (
          <div className="flex flex-col items-center text-center gap-4 py-6">
            <BsExclamationCircle className="text-5xl text-amber-500" />
            <h1 className="text-xl font-bold text-slate-800">
              {state === "used" ? t("rate_already_used") : t("rate_invalid_link")}
            </h1>
            <p className="text-sm text-slate-500 leading-relaxed">
              {state === "used" ? t("rate_already_used_text") : t("rate_invalid_link_text")}
            </p>
            <button
              onClick={() => navigate("/")}
              className="mt-2 rounded-full bg-teal_600 text-white font-bold text-sm px-8 py-3"
            >
              {t("go_to_main")}
            </button>
          </div>
        )}

        {state === "valid" && (
          <>
            <div className="text-center">
              <h1 className="text-2xl font-bold text-slate-800">
                {name ? `${name}, ` : ""}
                {t("rateUs")}
              </h1>
              <p className="text-sm text-slate-500 mt-2 leading-relaxed">{t("rate_intro")}</p>
            </div>

            <div className="flex flex-col items-center gap-2">
              <div className="flex gap-2" onMouseLeave={() => setHover(0)}>
                {[1, 2, 3, 4, 5].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setScore(s)}
                    onMouseEnter={() => setHover(s)}
                    aria-label={labels[s]}
                    className="p-1 transition-transform hover:scale-110"
                  >
                    <FaStar
                      className={`text-5xl sm:text-4xl transition-colors ${
                        s <= (hover || score) ? "text-yellow-400" : "text-slate-200"
                      }`}
                    />
                  </button>
                ))}
              </div>
              <p className="h-5 text-sm font-bold text-slate-600">{labels[hover || score] || ""}</p>
            </div>

            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t("rate_comment_optional")}
              rows={4}
              maxLength={2000}
              className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm
                         outline-none focus:border-teal_600 focus:bg-white resize-none"
            />

            {error && <p className="text-sm text-red-500 text-center -mt-2">{error}</p>}

            <button
              onClick={submit}
              disabled={busy}
              className="w-full rounded-full bg-teal_600 disabled:opacity-60 text-white font-bold py-3.5"
            >
              {busy ? "…" : t("rate_send")}
            </button>
          </>
        )}

        {state === "done" && (
          <div className="flex flex-col items-center text-center gap-4 py-6">
            <BsCheckCircleFill className="text-6xl text-teal_600" />
            <h1 className="text-2xl font-bold text-slate-800">{t("rate_thank_you")}</h1>
            <p className="text-sm text-slate-500 leading-relaxed">{t("rate_thank_you_text")}</p>
            <p className="text-xs text-slate-400">
              {t("rate_redirect_in", { n: left })}
            </p>
            <button
              onClick={() => navigate("/")}
              className="rounded-full border border-teal_600 text-teal_600 font-bold text-sm px-8 py-2.5"
            >
              {t("go_to_main")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Review;
